import React, { useState } from "react";
import { Row, Col, Card, Alert, Badge, Modal, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaUser,
  FaHome,
  FaBriefcase,
  FaDollarSign,
  FaBuilding,
  FaIdCard,
  FaFileContract,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaEye,
  FaShieldAlt,
} from "react-icons/fa";

const ReviewStep = ({
  formData,
  submitForm,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  // Calculate completion status
  const calculateCompletionStatus = () => {
    const sections = [
      { key: "personalInfo", required: true },
      { key: "address", required: true },
      { key: "employment", required: true },
      { key: "financial", required: true },
      { key: "business", required: false },
      { key: "identity", required: true },
      { key: "disclosures", required: true },
    ];

    let completed = 0;
    let total = sections.filter((s) => s.required).length;

    sections.forEach((section) => {
      if (
        section.required &&
        formData[section.key] &&
        Object.keys(formData[section.key]).length > 0
      ) {
        completed++;
      }
    });

    return { completed, total, percentage: (completed / total) * 100 };
  };

  // Calculate risk profile
  const calculateRiskProfile = () => {
    let riskScore = 0;

    if (formData.financial?.estimatedInvestment > 100000) riskScore += 2;
    else if (formData.financial?.estimatedInvestment > 50000) riskScore += 1;

    if (formData.financial?.riskPercentage > 50) riskScore += 2;
    else if (formData.financial?.riskPercentage > 25) riskScore += 1;

    if (formData.business?.companyName) riskScore += 1;

    if (riskScore >= 3) return { level: "high", color: "danger" };
    if (riskScore >= 1) return { level: "medium", color: "warning" };
    return { level: "low", color: "success" };
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await submitForm();
      setShowConfirmModal(false);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const completionStatus = calculateCompletionStatus();
  const riskProfile = calculateRiskProfile();

  // Format date for display
  const formatDate = (dateString) => {
    return dateString
      ? new Date(dateString).toLocaleDateString()
      : "Not provided";
  };

  // Format currency
  const formatCurrency = (amount) => {
    return amount ? `$${Number(amount).toLocaleString()}` : "Not provided";
  };

  const reviewSections = [
    {
      id: "personal",
      title: "Personal Information",
      icon: <FaUser className="text-primary" />,
      data: formData.personalInfo || {},
      fields: [
        { key: "fullName", label: "Full Name" },
        { key: "dateOfBirth", label: "Date of Birth", formatter: formatDate },
        { key: "nationality", label: "Nationality" },
        { key: "gender", label: "Gender" },
        { key: "maritalStatus", label: "Marital Status" },
        { key: "idNumber", label: "ID Number", sensitive: true },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
      ],
    },
    {
      id: "address",
      title: "Residential Address",
      icon: <FaHome className="text-primary" />,
      data: formData.address || {},
      fields: [
        { key: "street", label: "Street Address" },
        { key: "city", label: "City" },
        { key: "state", label: "State/Province" },
        { key: "postalCode", label: "Postal Code" },
        { key: "country", label: "Country" },
        { key: "proofOfAddress", label: "Proof of Address", type: "file" },
      ],
    },
    {
      id: "employment",
      title: "Employment Information",
      icon: <FaBriefcase className="text-primary" />,
      data: formData.employment || {},
      fields: [
        { key: "occupation", label: "Occupation" },
        { key: "employer", label: "Employer" },
        { key: "employmentStatus", label: "Employment Status" },
      ],
    },
    {
      id: "financial",
      title: "Financial Profile",
      icon: <FaDollarSign className="text-primary" />,
      data: formData.financial || {},
      fields: [
        {
          key: "estimatedInvestment",
          label: "Estimated Investment",
          formatter: formatCurrency,
        },
        { key: "netWorth", label: "Net Worth Range" },
        {
          key: "monthlyIncome",
          label: "Monthly Income",
          formatter: formatCurrency,
        },
        { key: "sourceOfFunds", label: "Source of Funds" },
        { key: "investmentPurpose", label: "Investment Purpose" },
        { key: "investmentExperience", label: "Investment Experience" },
        {
          key: "riskPercentage",
          label: "Risk Percentage",
          formatter: (val) => (val ? `${val}%` : "Not specified"),
        },
      ],
    },
  ];

  // Add business section if applicable
  if (formData.business && Object.keys(formData.business).length > 0) {
    reviewSections.push({
      id: "business",
      title: "Business Information",
      icon: <FaBuilding className="text-primary" />,
      data: formData.business || {},
      fields: [
        { key: "companyName", label: "Company Name" },
        { key: "website", label: "Website" },
        { key: "companyAddress", label: "Company Address" },
        { key: "corporatePhone", label: "Corporate Phone" },
        { key: "corporateEmail", label: "Corporate Email" },
      ],
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Row>
        <Col lg={10} className="mx-auto">
          {/* Completion Status */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Application Status</h5>
                <Badge
                  bg={
                    completionStatus.percentage === 100 ? "success" : "warning"
                  }
                >
                  {completionStatus.completed}/{completionStatus.total} Sections
                  Complete
                </Badge>
              </div>

              <div className="progress mb-3" style={{ height: "10px" }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${completionStatus.percentage}%` }}
                />
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted">
                    {completionStatus.percentage === 100
                      ? "All required sections completed"
                      : "Please complete all required sections before submitting"}
                  </small>
                </div>
                <div className="d-flex align-items-center">
                  <span className="badge bg-secondary me-2">Risk Level</span>
                  <Badge bg={riskProfile.color}>
                    {riskProfile.level.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Review Sections */}
          {reviewSections.map((section, index) => (
            <Card key={section.id} className="mb-4 border-0 bg-dark-subtle">
              <Card.Body className="p-4">
                <div
                  className="d-flex align-items-center justify-content-between mb-3 cursor-pointer"
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === section.id ? null : section.id
                    )
                  }
                >
                  <div className="d-flex align-items-center">
                    {section.icon}
                    <h6 className="mb-0 ms-2">{section.title}</h6>
                  </div>
                  <div className="d-flex align-items-center">
                    <Badge
                      bg={
                        Object.keys(section.data).length > 0
                          ? "success"
                          : "secondary"
                      }
                    >
                      {Object.keys(section.data).length > 0
                        ? "Complete"
                        : "Incomplete"}
                    </Badge>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="ms-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedSection(
                          expandedSection === section.id ? null : section.id
                        );
                      }}
                    >
                      <FaEye />
                    </Button>
                  </div>
                </div>

                {(expandedSection === section.id ||
                  Object.keys(section.data).length === 0) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="border-top pt-3">
                      {section.fields.map((field) => (
                        <div key={field.key} className="row mb-2">
                          <div className="col-sm-4">
                            <small className="text-muted fw-medium">
                              {field.label}
                            </small>
                          </div>
                          <div className="col-sm-8">
                            {field.type === "file" ? (
                              section.data[field.key] ? (
                                <div className="d-flex align-items-center">
                                  <FaCheckCircle className="text-success me-2" />
                                  <small>File uploaded</small>
                                </div>
                              ) : (
                                <small className="text-muted">
                                  No file uploaded
                                </small>
                              )
                            ) : field.sensitive ? (
                              <small>••••••••</small>
                            ) : (
                              <small>
                                {field.formatter
                                  ? field.formatter(section.data[field.key])
                                  : section.data[field.key] || "Not provided"}
                              </small>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </Card.Body>
            </Card>
          ))}

          {/* Identity Verification Status */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <FaIdCard className="text-primary me-2" />
                <h6 className="mb-0">Identity Verification</h6>
              </div>

              <div className="d-flex align-items-center mb-2">
                {formData.identity?.idDocument ? (
                  <FaCheckCircle className="text-success me-2" />
                ) : (
                  <FaExclamationTriangle className="text-warning me-2" />
                )}
                <small>
                  ID Document:{" "}
                  {formData.identity?.idDocument ? "Uploaded" : "Missing"}
                </small>
              </div>
            </Card.Body>
          </Card>

          {/* Disclosures Status */}
          <Card className="mb-4 border-0 bg-dark-subtle">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <FaFileContract className="text-primary me-2" />
                <h6 className="mb-0">Legal Disclosures</h6>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-2">
                    {formData.disclosures?.termsAccepted ? (
                      <FaCheckCircle className="text-success me-2" />
                    ) : (
                      <FaExclamationTriangle className="text-warning me-2" />
                    )}
                    <small>Terms & Conditions</small>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    {formData.disclosures?.privacyAccepted ? (
                      <FaCheckCircle className="text-success me-2" />
                    ) : (
                      <FaExclamationTriangle className="text-warning me-2" />
                    )}
                    <small>Privacy Policy</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-2">
                    {formData.disclosures?.riskDisclosureAccepted ? (
                      <FaCheckCircle className="text-success me-2" />
                    ) : (
                      <FaExclamationTriangle className="text-warning me-2" />
                    )}
                    <small>Risk Disclosure</small>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    {formData.disclosures?.digitalSignature ? (
                      <FaCheckCircle className="text-success me-2" />
                    ) : (
                      <FaExclamationTriangle className="text-warning me-2" />
                    )}
                    <small>Digital Signature</small>
                  </div>
                </div>
              </div>

              {formData.disclosures?.digitalSignature && (
                <Alert variant="success" className="mt-3 mb-0">
                  <small>
                    <FaCheckCircle className="me-2" />
                    Digitally signed by:{" "}
                    <strong>{formData.disclosures.digitalSignature}</strong>
                    <br />
                    Date: {formatDate(formData.disclosures.signatureDate)}
                  </small>
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Security Notice */}
          <Alert variant="info" className="mb-4">
            <FaShieldAlt className="me-2" />
            <strong>Data Security:</strong> Your information is encrypted and
            stored securely. We use bank-level security to protect your personal
            and financial data.
          </Alert>

          {/* Final Submission */}
          {completionStatus.percentage === 100 ? (
            <Alert variant="success" className="mb-4">
              <FaCheckCircle className="me-2" />
              <strong>Ready for submission!</strong> Please review all
              information above before final submission.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-4">
              <FaExclamationTriangle className="me-2" />
              <strong>Incomplete application:</strong> Please complete all
              required sections before submitting.
            </Alert>
          )}
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaCheckCircle size={48} className="text-success mb-3" />
            <h5>Submit KYC Application</h5>
            <p className="text-muted">
              Are you sure you want to submit your KYC application? Once
              submitted, you cannot modify the information.
            </p>
            <Alert variant="info" className="text-start">
              <small>
                <strong>What happens next:</strong>
                <br />
                • Your application will be reviewed by our compliance team
                <br />
                • You'll receive email updates on the status
                <br />
                • Verification typically takes 1-3 business days
                <br />• You'll be notified once approved
              </small>
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="spinner-border spinner-border-sm me-2" />
                Submitting...
              </>
            ) : (
              "Confirm & Submit"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default ReviewStep;
